import React, { useEffect } from 'react';
import Input from '../../../shared/forms/Input';
import Modal, { ModalCloseButton } from '../../../shared/modals/Modal';
import { ReactComponent as Spinner } from '../../../assets/icons/spinner.svg';
import { useMessage } from '../../../contexts/MessageContext';

const FIELD_GROUPS = [
  [
    { key: 'customerName', label: 'Customer name' },
    { key: 'customerCompany', label: 'Company' },
  ],
  [
    { key: 'customerAddressLine1', label: 'Address line 1' },
    { key: 'customerAddressLine2', label: 'Address line 2' },
  ],
  [
    { key: 'customerCityCountry', label: 'City / region / country' },
    { key: 'customerPhone', label: 'Phone' },
  ],
  [
    { key: 'customerEmail', label: 'Billing email', type: 'email' },
    { key: 'customerWebsite', label: 'Website', placeholder: 'https://example.com' },
  ],
];

const InvoiceTemplateModal = ({ setInvoiceTemplateModalVisible }) => {
  const showMessage = useMessage();
  const [templateData, setTemplateData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewHtml, setPreviewHtml] = React.useState('');
  const closeModal = () => setInvoiceTemplateModalVisible(false);

  useEffect(() => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-invoice-template', {
      headers: { Authorization: localStorage.getItem('token') },
    })
      .then((response) => response.json())
      .then((data) => {
        setTemplateData(data.fields || {});
        setLoading(false);
      })
      .catch(() => {
        setTemplateData({});
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (loading || !templateData) return undefined;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      setPreviewLoading(true);

      fetch(process.env.REACT_APP_SERVER_URL + '/api/render-invoice-template-preview', {
        method: 'POST',
        headers: {
          Authorization: localStorage.getItem('token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ templateData }),
        signal: controller.signal,
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            setPreviewHtml(data.html);
          }
          setPreviewLoading(false);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            setPreviewLoading(false);
          }
        });
    }, 120);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [templateData, loading]);

  const handleFieldChange = (field) => (event) => {
    const nextValue = event.target.value;
    setTemplateData((current) => ({
      ...current,
      [field]: nextValue,
    }));
  };

  return (
    <Modal isOpen={true} onClose={closeModal} className="modal--wide modal--full-height">
      <div className="modal invoice-template-modal">
        <div className="modal__header">
          <div>
            <h3 className="margin-none">Edit invoice template</h3>
          </div>
          <ModalCloseButton
            onClick={closeModal}
            label="Close invoice template modal"
          />
        </div>

        <div className="modal__body">
          <form
            id="invoice-template-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!templateData || saving) return;
              setSaving(true);
              fetch(process.env.REACT_APP_SERVER_URL + '/api/save-invoice-template', {
                method: 'POST',
                headers: {
                  Authorization: localStorage.getItem('token'),
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ fields: templateData }),
              })
                .then((r) => r.json())
                .then(() => {
                  showMessage('Invoice template details saved.');
                  closeModal();
                })
                .catch(() => {
                  showMessage('Failed to save. Please try again.');
                  setSaving(false);
                });
            }}
          >
            <div className="modal-grid invoice-template-layout">
              <div className="modal-section">
                <h4 className="modal-section__title">Customer details</h4>

                {loading && (
                  <div className="simple-row center">
                    <Spinner className="modal-submit-spinner" />
                    <p className="margin-none">Loading...</p>
                  </div>
                )}

                {!loading && templateData && (
                  <div className="invoice-template-fields">
                    {FIELD_GROUPS.map((row, rowIndex) => (
                      <div className="invoice-template-fields__row" key={rowIndex}>
                        {row.map((field) => (
                          <Input
                            key={field.key}
                            label={field.label}
                            value={templateData[field.key]}
                            onChange={handleFieldChange(field.key)}
                            placeholder={field.placeholder}
                            type={field.type}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-section">
                <h4 className="modal-section__title">Invoice preview</h4>

                <div className="invoice-template-preview-shell">
                  {previewLoading && !previewHtml && (
                    <div className="simple-row center invoice-template-preview__loading">
                      <Spinner className="modal-submit-spinner" />
                      <p className="margin-none">Rendering preview...</p>
                    </div>
                  )}

                  {previewHtml && (
                    <div className="invoice-template-preview-viewport">
                      <div className="invoice-template-preview-canvas">
                        <iframe
                          title="Invoice template preview"
                          className="invoice-template-preview-iframe"
                          srcDoc={previewHtml}
                        />
                      </div>
                    </div>
                  )}

                  {previewLoading && previewHtml && (
                    <p className="footnote invoice-template-preview__status">
                      Updating preview...
                    </p>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="modal__footer">
          <button
            type="button"
            className="btn secondary"
            onClick={closeModal}
          >
            Discard changes
          </button>
          <button
            type="submit"
            form="invoice-template-form"
            className="btn primary"
            disabled={saving}
          >
            Save changes
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceTemplateModal;
